import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HeartPulse, Plus, ShieldCheck } from 'lucide-react';
import { WellbeingAlerts } from './WellbeingAlerts';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ChipToggle,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  LoadingState,
  Select,
  type Column,
  useToast,
} from '@/components/ui';
import {
  type WellbeingSession,
  useWellbeingAccess,
  useWellbeingActions,
  useWellbeingSessions,
} from '@/features/admin-v2/api';
import { useMe, useSession } from '@/features/auth/session-context';
import { formatRM } from '@/features/billing/money';
import {
  KIND_LABEL,
  useSessionTypes,
  useWellbeingPractitioners,
} from '@/features/wellbeing-setup/api';
import { useBranch } from '@/features/branch/branch-context';
import type { PatientSummary } from '@/features/patients/types';
import { PageContainer } from '@/layouts/PageContainer';
import { api, isApiError } from '@/lib/api';
import { klDate, klTime, toIso, todayKl } from '@/pages/calendar/time';
import local from '@/pages/finance/finance.module.css';
import { dayLabel } from '@/pages/ops/register-format';
import { PatientPicker } from '@/pages/queue/PatientPicker';
import styles from '@/pages/queue/queue.module.css';

export function WellbeingPage({ mode }: { mode: 'sessions' | 'access' }) {
  return mode === 'sessions' ? <Sessions /> : <Access />;
}

const STATUS: Record<
  string,
  { label: string; tone: 'neutral' | 'info' | 'warning' | 'success' | 'danger' }
> = {
  booked: { label: 'Scheduled', tone: 'neutral' },
  arrived: { label: 'Arrived', tone: 'info' },
  in_session: { label: 'In session', tone: 'info' },
  no_show: { label: 'No-show', tone: 'warning' },
  closed: { label: 'Closed', tone: 'success' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};
/** RL-2020: who cancelled, from the API's `cancelledBy`. */
const CANCELLED_BY: Record<string, string> = {
  patient: 'Cancelled by patient',
  clinic: 'Cancelled by the clinic',
};

/**
 * The status shown for a session: closed first, then who cancelled it
 * (plain "Cancelled" when the API does not say, or says something new).
 */
function statusOf(s: WellbeingSession) {
  if (s.closedAt) return STATUS.closed;
  if (s.status === 'cancelled')
    return {
      ...STATUS.cancelled,
      label:
        (s.cancelledBy && CANCELLED_BY[s.cancelledBy]) ||
        STATUS.cancelled.label,
    };
  return STATUS[s.status] ?? { label: s.status, tone: 'neutral' as const };
}
const TYPE: Record<string, string> = { in_person: 'In person', video: 'Video' };

function useFail() {
  const toast = useToast();
  return (err: unknown) =>
    toast.show({
      tone: 'error',
      title: 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
}

function when(iso: string) {
  return klDate(iso) === todayKl()
    ? `Today ${klTime(iso)}`
    : `${dayLabel(iso)} ${klTime(iso)}`;
}

/** "Sessions · restricted compartment" (prototype sessions()). */
function Sessions() {
  const { selected: branch } = useBranch();
  const { hasPermission } = useSession();
  const sessions = useWellbeingSessions();
  const [booking, setBooking] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  if (!branch)
    return (
      <PageContainer>
        <p className={styles.warnNote}>
          Choose a branch to open the wellbeing compartment.
        </p>
      </PageContainer>
    );
  const columns: Column<WellbeingSession>[] = [
    { key: 'date', header: 'Date', render: (s) => when(s.startsAt) },
    {
      key: 'patient',
      header: 'Patient',
      render: (s) => (
        <>
          <p className={styles.name}>{s.patient.fullName}</p>
          <p className={styles.sub}>{s.serviceCode}</p>
        </>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (s) => `${s.practitioner.fullName} · ${TYPE[s.type] ?? s.type}`,
    },
    {
      key: 'screening',
      header: 'Screening',
      render: (s) =>
        // RL-1875 (gap #87): an urgent slot is "Urgent"; the screening
        // score shows on its own, never as a "crisis" it does not say.
        s.urgent || s.screening?.score != null ? (
          <span className={local.badges}>
            {s.urgent && <Badge tone="danger">Urgent</Badge>}
            {s.screening?.score != null && (
              <Badge>{`${s.screening.tool} ${s.screening.score}`}</Badge>
            )}
          </span>
        ) : (
          '-'
        ),
    },
    {
      key: 'plan',
      header: 'Plan',
      render: (s) =>
        s.planSharedAt ? <Badge tone="success">Shared to app</Badge> : '-',
    },
    {
      key: 'status',
      header: 'Status',
      render: (s) => {
        const st = statusOf(s);
        return <Badge tone={st.tone}>{st.label}</Badge>;
      },
    },
    {
      key: 'open',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (s) => (
        <Button size="sm" onClick={() => setOpen(s.id)}>
          Open
        </Button>
      ),
    },
  ];
  return (
    <PageContainer>
      {hasPermission('wellbeing_sessions:manage') && <WellbeingAlerts />}
      <Card
        title="Sessions · restricted compartment"
        flush
        actions={
          <>
            <Badge tone="brand">Wellbeing</Badge>
            {hasPermission('wellbeing_sessions:manage') && (
              <Button
                size="sm"
                variant="primary"
                icon={<Plus size={16} />}
                onClick={() => setBooking(true)}
              >
                Book session
              </Button>
            )}
          </>
        }
      >
        <DataTable
          caption="Wellbeing sessions"
          columns={columns}
          rows={sessions.data}
          rowKey={(s) => s.id}
          loading={sessions.isLoading}
          error={sessions.error}
          onRetry={() => void sessions.refetch()}
          empty={
            <EmptyState
              icon={<HeartPulse size={22} />}
              title="No sessions"
              body="Book a counselling, psychology or psychiatry session to start."
            />
          }
        />
      </Card>
      <p className={local.note}>
        The GP encounter view and every panel claim show only the service code
        for these visits. Releasing content to the general record is a separate,
        logged action with the patient's consent.
      </p>
      {booking && <BookSession onClose={() => setBooking(false)} />}
      {open && <SessionDrawer id={open} onClose={() => setOpen(null)} />}
    </PageContainer>
  );
}

function useWellbeingMutations() {
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: ['wellbeing'] });
  return {
    book: useMutation({
      mutationFn: (v: unknown) => api.post('/wellbeing/sessions', v),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: (v: { id: string; body: Record<string, unknown> }) =>
        api.patch(`/wellbeing/sessions/${v.id}`, v.body),
      onSuccess: done,
    }),
    grant: useMutation({
      mutationFn: (v: unknown) => api.post('/wellbeing/access', v),
      onSuccess: done,
    }),
    revoke: useMutation({
      mutationFn: (id: string) => api.post(`/wellbeing/access/${id}/revoke`),
      onSuccess: done,
    }),
    // RL-1877: the clinic cancels or moves a booked session.
    cancel: useMutation({
      mutationFn: (v: { id: string; reason?: string }) =>
        api.post(`/wellbeing/sessions/${v.id}/cancel`, { reason: v.reason }),
      onSuccess: done,
    }),
    move: useMutation({
      mutationFn: (v: { id: string; startsAt: string }) =>
        api.post(`/wellbeing/sessions/${v.id}/move`, { startsAt: v.startsAt }),
      onSuccess: done,
    }),
  };
}

function BookSession({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const fail = useFail();
  const { book } = useWellbeingMutations();
  const me = useMe();
  // RL-1870 (decision D16): staff pick the session type (its price is
  // billed) and a practitioner set up for its kind.
  const types = useSessionTypes();
  const practitioners = useWellbeingPractitioners();
  const [patient, setPatient] = useState<PatientSummary | null>(null);
  const [typeId, setTypeId] = useState('');
  const [practitionerId, setPractitionerId] = useState(me.id);
  const [date, setDate] = useState(todayKl());
  const [time, setTime] = useState('');
  const [type, setType] = useState<'in_person' | 'video'>('in_person');
  const [urgent, setUrgent] = useState(false);
  const sessionType = types.data?.find((x) => x.id === typeId) ?? null;
  const able = (practitioners.data ?? []).filter(
    (p) => !sessionType || p.kinds.includes(sessionType.practitionerKind),
  );
  const modes = sessionType?.modes ?? ['in_person', 'video'];
  const submit = () => {
    if (!patient || !sessionType || !date || !/^\d{2}:\d{2}$/.test(time))
      return toast.show({
        tone: 'error',
        title: 'Choose the patient, session type, date and time',
      });
    if (!able.some((p) => p.staffUserId === practitionerId))
      return toast.show({
        tone: 'error',
        title: 'Choose a practitioner for this session type',
      });
    if (!modes.includes(type))
      return toast.show({
        tone: 'error',
        title: 'This session type is not offered this way',
      });
    book
      .mutateAsync({
        patientId: patient.id,
        sessionTypeId: sessionType.id,
        practitionerId,
        startsAt: toIso(date, time),
        type,
        urgent,
      })
      .then(() => {
        toast.show({ tone: 'success', title: 'Session booked' });
        onClose();
      })
      .catch(fail);
  };
  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title="Book session"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={book.isPending} onClick={submit}>
            Book
          </Button>
        </>
      }
    >
      <div className={local.form}>
        <PatientPicker value={patient} onChange={setPatient} />
        <Field label="Session type">
          <Select
            value={typeId}
            placeholder={
              types.isLoading ? 'Loading…' : 'Choose the session type'
            }
            options={(types.data ?? []).map((x) => ({
              value: x.id,
              label: `${x.name} · ${KIND_LABEL[x.practitionerKind]} · ${x.durationMinutes} min · ${formatRM(x.priceSen)}`,
            }))}
            onChange={(e) => {
              setTypeId(e.target.value);
              // Review fix: keep "Where" to a mode the new type offers.
              const next = types.data?.find((x) => x.id === e.target.value);
              if (next && !next.modes.includes(type)) setType(next.modes[0]);
            }}
          />
        </Field>
        <Field
          label="Practitioner"
          hint={
            sessionType && !able.length
              ? 'Nobody is set up for this kind yet: see Wellbeing set-up.'
              : undefined
          }
        >
          <Select
            value={practitionerId}
            options={able.map((p) => ({ value: p.staffUserId, label: p.name }))}
            placeholder="Choose the practitioner"
            onChange={(e) => setPractitionerId(e.target.value)}
          />
        </Field>
        <Field label="Date">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <Field label="Time">
          <Input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
        </Field>
        <ChipToggle
          label="Where"
          single
          options={(
            [
              { value: 'in_person', label: 'In person' },
              { value: 'video', label: 'Video' },
            ] as { value: 'in_person' | 'video'; label: string }[]
          ).filter((o) => modes.includes(o.value))}
          selected={[type]}
          onChange={(v) => v[0] && setType(v[0])}
        />
        <ChipToggle
          label="Urgent slot"
          single
          options={[
            { value: 'no', label: 'No' },
            { value: 'yes', label: 'Urgent' },
          ]}
          selected={[urgent ? 'yes' : 'no']}
          onChange={(v) => setUrgent(v[0] === 'yes')}
        />
      </div>
    </Dialog>
  );
}

interface SessionDetail extends WellbeingSession {
  notes: string | null;
  plan: string | null;
}

function SessionDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { hasPermission } = useSession();
  const toast = useToast();
  const fail = useFail();
  const q = useQuery({
    queryKey: ['wellbeing', 'session', id],
    queryFn: ({ signal }) =>
      api
        .get<SessionDetail>(`/wellbeing/sessions/${id}`, { signal })
        .then((r) => r.data),
  });
  const actions = useWellbeingActions();
  const { update, cancel, move } = useWellbeingMutations();
  const s = q.data;
  const [changing, setChanging] = useState<'move' | 'cancel' | null>(null);
  // Review fix: when the window opened, so a started session offers no move.
  const [openedAt] = useState(() => Date.now());
  const [moveDate, setMoveDate] = useState('');
  const [moveTime, setMoveTime] = useState('');
  const can =
    hasPermission('wellbeing_sessions:manage') &&
    !!s?.canReadNotes &&
    !s?.closedAt;
  const [notes, setNotes] = useState<string | null>(null);
  const [plan, setPlan] = useState<string | null>(null);
  const [tool, setTool] = useState<string | null>(null);
  const [score, setScore] = useState<string | null>(null);
  const save = (extra: Record<string, unknown> = {}) => {
    if (!s) return;
    const body: Record<string, unknown> = { ...extra };
    if (notes !== null) body.notes = notes;
    if (plan !== null) body.plan = plan;
    const t = tool ?? s.screening?.tool;
    if (score !== null && score.trim() !== '') {
      const n = Number(score);
      if (!Number.isInteger(n) || n < 0 || n > 27 || !t)
        return toast.show({
          tone: 'error',
          title: 'Choose the tool and a score from 0 to 27',
        });
      body.screeningTool = t;
      body.screeningScore = n;
    }
    update
      .mutateAsync({ id: s.id, body })
      .then(() => toast.show({ tone: 'success', title: 'Saved' }))
      .catch(fail);
  };
  const ok = (title: string) => toast.show({ tone: 'success', title });
  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title={s ? `${s.patient.fullName} · ${when(s.startsAt)}` : 'Session'}
      footer={
        can && (
          <Button
            variant="primary"
            loading={update.isPending}
            onClick={() => save()}
          >
            Save notes
          </Button>
        )
      }
    >
      {!s ? (
        <LoadingState />
      ) : (
        <div className={local.form}>
          <dl className={local.kv}>
            <dt>Practitioner</dt>
            <dd>{s.practitioner.fullName}</dd>
            <dt>Type</dt>
            <dd>{TYPE[s.type] ?? s.type}</dd>
            <dt>Billing</dt>
            <dd className="mono">{s.serviceCode}</dd>
            <dt>Status</dt>
            <dd>{statusOf(s).label}</dd>
          </dl>
          {can &&
            s.status === 'booked' &&
            new Date(s.startsAt).getTime() > openedAt && (
              <div className={local.form}>
                {changing === null ? (
                  <span className={local.acts}>
                    <Button size="sm" onClick={() => setChanging('move')}>
                      Move session
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => setChanging('cancel')}
                    >
                      Cancel session
                    </Button>
                  </span>
                ) : changing === 'move' ? (
                  <>
                    <div className={local.twoCol}>
                      <Field label="New date">
                        <Input
                          type="date"
                          value={moveDate}
                          onChange={(e) => setMoveDate(e.target.value)}
                        />
                      </Field>
                      <Field label="New time">
                        <Input
                          type="time"
                          value={moveTime}
                          onChange={(e) => setMoveTime(e.target.value)}
                        />
                      </Field>
                    </div>
                    <p className={local.note}>
                      The patient's app is told the new time ("your
                      appointment").
                    </p>
                    <span className={local.acts}>
                      <Button size="sm" onClick={() => setChanging(null)}>
                        Back
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        loading={move.isPending}
                        onClick={() => {
                          if (!moveDate || !/^\d{2}:\d{2}$/.test(moveTime))
                            return toast.show({
                              tone: 'error',
                              title: 'Choose the new date and time',
                            });
                          void move
                            .mutateAsync({
                              id: s.id,
                              startsAt: toIso(moveDate, moveTime),
                            })
                            .then(() => {
                              ok('Session moved; the patient is told');
                              setChanging(null);
                            })
                            .catch(fail);
                        }}
                      >
                        Move and tell the patient
                      </Button>
                    </span>
                  </>
                ) : (
                  <>
                    <p className={local.note}>
                      The patient's app is told the appointment was cancelled
                      (never the reason).
                    </p>
                    <span className={local.acts}>
                      <Button size="sm" onClick={() => setChanging(null)}>
                        Back
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        loading={cancel.isPending}
                        onClick={() =>
                          void cancel
                            .mutateAsync({ id: s.id })
                            .then(() => {
                              ok('Session cancelled; the patient is told');
                              onClose();
                            })
                            .catch(fail)
                        }
                      >
                        Cancel and tell the patient
                      </Button>
                    </span>
                  </>
                )}
              </div>
            )}
          {!s.canReadNotes ? (
            <p className={local.note}>
              Notes stay with the practitioner of record. You see the visit and
              its service code only.
            </p>
          ) : (
            <>
              {can && (
                <ChipToggle
                  label="Visit"
                  single
                  options={[
                    { value: 'arrived', label: 'Arrived' },
                    { value: 'in_session', label: 'In session' },
                    { value: 'no_show', label: 'No-show' },
                  ]}
                  selected={[s.status]}
                  onChange={(v) => v[0] && save({ status: v[0] })}
                />
              )}
              <Field label="Session notes">
                <textarea
                  className={local.textarea}
                  value={notes ?? s.notes ?? ''}
                  disabled={!can}
                  maxLength={8000}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Field>
              <Field label="Plan (can be shared to the patient app)">
                <textarea
                  className={local.textarea}
                  value={plan ?? s.plan ?? ''}
                  disabled={!can}
                  maxLength={4000}
                  onChange={(e) => setPlan(e.target.value)}
                />
              </Field>
              <div className={local.twoCol}>
                <Field label="Screening tool">
                  <Select
                    value={tool ?? s.screening?.tool ?? ''}
                    disabled={!can}
                    placeholder="Choose…"
                    options={[
                      { value: 'PHQ-9', label: 'PHQ-9' },
                      { value: 'GAD-7', label: 'GAD-7' },
                    ]}
                    onChange={(e) => setTool(e.target.value)}
                  />
                </Field>
                <Field label="Score">
                  <Input
                    inputMode="numeric"
                    disabled={!can}
                    value={
                      score ??
                      (s.screening?.score != null
                        ? String(s.screening.score)
                        : '')
                    }
                    onChange={(e) => setScore(e.target.value)}
                  />
                </Field>
              </div>
              {can && (
                <span className={local.acts}>
                  {!s.urgent && (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() =>
                        void actions.crisis
                          .mutateAsync(s.id)
                          .then(() => ok('Crisis pathway started'))
                          .catch(fail)
                      }
                    >
                      Crisis pathway
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() =>
                      void actions.sharePlan
                        .mutateAsync(s.id)
                        .then(() => ok('Plan shared to the app'))
                        .catch(fail)
                    }
                  >
                    Share plan
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() =>
                      void actions.close
                        .mutateAsync(s.id)
                        .then(() => {
                          ok('Session closed; service code billed');
                          onClose();
                        })
                        .catch(fail)
                    }
                  >
                    Close session
                  </Button>
                </span>
              )}
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}

/** "Who can open Wellbeing records" (prototype mhaccess()). */
function Access() {
  const { hasPermission } = useSession();
  const access = useWellbeingAccess();
  const { revoke } = useWellbeingMutations();
  const toast = useToast();
  const fail = useFail();
  const [granting, setGranting] = useState(false);
  const canManage = hasPermission('wellbeing_access:manage');
  type Row = {
    id: string;
    person: string;
    access: { label: string; tone: 'success' | 'neutral' | 'warning' };
    since: string | null;
    grantId: string | null;
  };
  const rows: Row[] = [
    // A practitioner with roles at both branches is listed once.
    ...[
      ...new Map(
        (access.data?.practitioners ?? []).map((p) => [p.id, p]),
      ).values(),
    ].map((p) => ({
      id: `p-${p.id}`,
      person: p.fullName,
      access: {
        label: 'Full · practitioner of record',
        tone: 'success' as const,
      },
      since: null,
      grantId: null,
    })),
    ...(
      (access.data?.grants ?? []) as {
        id: string;
        staff: { fullName: string };
        level: string;
        grantedAt: string;
        revokedAt: string | null;
      }[]
    )
      .filter((g) => !g.revokedAt)
      .map((g) => ({
        id: g.id,
        person: g.staff.fullName,
        access:
          g.level === 'practitioner'
            ? { label: 'Full · granted', tone: 'warning' as const }
            : { label: 'Summary only', tone: 'neutral' as const },
        since: g.grantedAt,
        grantId: g.id,
      })),
  ];
  return (
    <PageContainer>
      <Card
        title="Who can open Wellbeing records"
        flush
        actions={
          canManage ? (
            <Button
              size="sm"
              variant="primary"
              icon={<Plus size={16} />}
              onClick={() => setGranting(true)}
            >
              Grant access
            </Button>
          ) : (
            // RL-1878 (gap #88, decision D16): the practitioners govern it.
            <span className={styles.sub}>
              Managed by the mental-health practitioners
            </span>
          )
        }
      >
        <DataTable
          caption="Wellbeing access"
          rows={access.data ? rows : undefined}
          rowKey={(r) => r.id}
          loading={access.isLoading}
          error={access.error}
          onRetry={() => void access.refetch()}
          empty={
            <EmptyState
              icon={<ShieldCheck size={22} />}
              title="Nobody has access yet"
            />
          }
          columns={[
            {
              key: 'p',
              header: 'Person',
              render: (r) => <strong>{r.person}</strong>,
            },
            {
              key: 'a',
              header: 'Access',
              render: (r) => (
                <Badge tone={r.access.tone}>{r.access.label}</Badge>
              ),
            },
            {
              key: 's',
              header: 'Since',
              render: (r) => (r.since ? dayLabel(r.since) : '-'),
            },
            {
              key: 'x',
              header: <span className="visually-hidden">Actions</span>,
              align: 'right',
              render: (r) =>
                r.grantId && canManage ? (
                  <Button
                    size="sm"
                    variant="danger"
                    loading={revoke.isPending}
                    onClick={() =>
                      void revoke
                        .mutateAsync(r.grantId as string)
                        .then(() =>
                          toast.show({
                            tone: 'success',
                            title: 'Access revoked',
                          }),
                        )
                        .catch(fail)
                    }
                  >
                    Revoke
                  </Button>
                ) : null,
            },
          ]}
        />
      </Card>
      <p className={styles.warnNote}>
        Every opening of wellbeing notes is logged in the audit log.
      </p>
      {granting && <GrantAccess onClose={() => setGranting(false)} />}
    </PageContainer>
  );
}

function GrantAccess({ onClose }: { onClose: () => void }) {
  // RL-1878: access goes only to Mental health role holders (the API checks).
  const people = useWellbeingPractitioners();
  const toast = useToast();
  const fail = useFail();
  const { grant } = useWellbeingMutations();
  const [staffUserId, setStaff] = useState('');
  const [level, setLevel] = useState<'practitioner' | 'summary'>('summary');
  return (
    <Dialog
      open
      onClose={onClose}
      title="Grant access"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={grant.isPending}
            onClick={() => {
              if (!staffUserId)
                return toast.show({ tone: 'error', title: 'Choose a person' });
              void grant
                .mutateAsync({ staffUserId, level })
                .then(() => {
                  toast.show({ tone: 'success', title: 'Access granted' });
                  onClose();
                })
                .catch(fail);
            }}
          >
            Grant
          </Button>
        </>
      }
    >
      <div className={local.form}>
        <p className={local.note}>
          Only staff with the Mental health role can open Wellbeing records.
        </p>
        <Field label="Person">
          <Select
            value={staffUserId}
            placeholder="Choose…"
            options={(people.data ?? []).map((p) => ({
              value: p.staffUserId,
              label: p.name,
            }))}
            onChange={(e) => setStaff(e.target.value)}
          />
        </Field>
        <ChipToggle
          label="Access"
          single
          options={[
            { value: 'summary', label: 'Summary only' },
            { value: 'practitioner', label: 'Full notes' },
          ]}
          selected={[level]}
          onChange={(v) => v[0] && setLevel(v[0])}
        />
      </div>
    </Dialog>
  );
}
